import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    findingPlanumEntity,
    findingProfileEntity,
    type FindingPlanumDocType,
    type FindingProfileDocType
} from '@ast-data/shared';
import { useFamilyDescriptions } from '../findings/useFamilyDescriptions';
import { useFindingRefResolver } from '../findings/findingRefLabels';
import { visibleHistoryFields } from './internalHistoryFields';
import { historyValueFormat } from './historyValueFormats';
import type { HistoryField } from './HistoryDialog';

// ---------------------------------------------------------------------------
/**
 * Befund references in a version history.
 *
 * Every reference in this app is stored as ids and NOTHING else — a Planum's
 * "identisch mit" has no readable twin field the way `editor` sits beside
 * `editorId`. Left out of the dialog (as bare UUIDs would have to be), linking a
 * Planum produced a version that claimed "keine Änderungen"; printed raw, it
 * produced a row of UUIDs. So the history resolves them the same way the grids
 * and the pickers do: through `useFindingRefResolver`, into "Bef. 7000, Fl. 1,
 * Pl. 1".
 *
 * The arrays are also pooled, because a control the form shows as ONE list is
 * stored as two or three (a ref may point at a Planum, a Profil or a whole
 * Befund). `HistoryField.keys` is what carries that pooling into the dialog.
 */
// ---------------------------------------------------------------------------

/** The three arrays a Fund/Foto/Zeichenblatt spreads its Befund refs over. */
export const RECORD_REF_KEYS = ['planumIds', 'profileIds', 'findingIds'];

/** A description relation, as the form shows it and as the record stores it. */
interface RelationGroup {
    /** The name the form uses — also the field's label id suffix. */
    name: string;
    keys: string[];
}

/**
 * The symmetric Planum/Profil relations. Each is one control in the form and two
 * arrays in the record, one per kind of description it may point at.
 */
const DESCRIPTION_RELATIONS: RelationGroup[] = [
    {
        name: 'identicalWith',
        keys: ['identicalWithPlanumIds', 'identicalWithProfileIds']
    },
    {
        name: 'partOf',
        keys: ['partOfPlanumIds', 'partOfProfileIds']
    },
    {
        name: 'inProfileWith',
        keys: ['inProfileWithPlanumIds', 'inProfileWithProfileIds']
    }
];

const RELATION_KEYS = DESCRIPTION_RELATIONS.flatMap((group) => group.keys);

/**
 * Resolve any Befund reference id to its label ("Bef. 7000, Fl. 1, Pl. 1"),
 * falling back to the raw id for a description that has since been deleted —
 * which is exactly the case where the id is the only thing left to show.
 */
export function useFindingRefLabel(projectId: string): (id: string) => string {
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);
    const refs = useFindingRefResolver(findings, planums, profiles);
    return useCallback((id: string) => refs.get(id)?.label ?? id, [refs]);
}

/**
 * The single history field standing for a record's Befund references — the three
 * ref arrays pooled and resolved, as one list of added/removed descriptions.
 *
 * `headerId` is the i18n id the record's own grid column uses for them, so the
 * history calls them what the table calls them.
 */
export function useRecordRefHistoryField(
    projectId: string,
    headerId: string
): HistoryField {
    const intl = useIntl();
    const resolveId = useFindingRefLabel(projectId);
    return useMemo(
        () => ({
            key: 'findingRefs',
            keys: RECORD_REF_KEYS,
            label: intl.formatMessage({ id: headerId }),
            resolveId
        }),
        [intl, headerId, resolveId]
    );
}

/**
 * Every field a Planum's / Profil's history dialog lists, with the three
 * relation controls in the place their arrays occupy in the record.
 *
 * `prefix` doubles as the label namespace, which is why it is the SCREEN's name
 * ('planum'/'profile') rather than the entity's (`findingPlanum`).
 */
export function useDescriptionHistoryFields(
    projectId: string,
    kind: 'planum' | 'profile'
): HistoryField[] {
    const intl = useIntl();
    const resolveId = useFindingRefLabel(projectId);
    const entity =
        kind === 'planum' ? findingPlanumEntity : findingProfileEntity;
    const entityType = entity.name;
    const historyFields =
        entity.historyFields as readonly (keyof (FindingPlanumDocType &
            FindingProfileDocType) &
            string)[];
    return useMemo(() => {
        const emitted = new Set<string>();
        const fields: HistoryField[] = [];
        // `keep` holds the relation arrays back from the internal-field filter:
        // they read as UUIDs raw, which is what got them filtered, and resolved
        // they are the whole point of this list.
        for (const key of visibleHistoryFields(historyFields, RELATION_KEYS)) {
            const group = DESCRIPTION_RELATIONS.find((candidate) =>
                candidate.keys.includes(key)
            );
            if (!group) {
                fields.push({
                    key,
                    label: intl.formatMessage({ id: `${kind}.field.${key}` }),
                    format: historyValueFormat(entityType, key)
                });
                continue;
            }
            // Both arrays of a relation land on the FIRST of them, so the change
            // reads as one list rather than as two half-lists.
            if (emitted.has(group.name)) {
                continue;
            }
            emitted.add(group.name);
            fields.push({
                key: group.name,
                keys: group.keys,
                label: intl.formatMessage({
                    id: `${kind}.field.${group.name}`
                }),
                resolveId
            });
        }
        return fields;
    }, [entityType, historyFields, intl, kind, resolveId]);
}
